/**
 * Read-only view of the feature declarations, as this environment
 * resolves them.
 *
 * Deliberately has no edit surface: features change through pull requests against
 * `modules/features.yaml`, and the reviewability of that file is the
 * control. What the page adds over reading the YAML is the three things the
 * file cannot tell you:
 *
 *  - how each feature resolves *here*, in this running environment;
 *  - which `FEATURES_FORCE_OFF` entries are actually in force — and which
 *    were mistyped and therefore did nothing. During an incident that is the
 *    difference between "the kill switch worked" and looking elsewhere for an
 *    hour;
 *  - which allowlist entries match no known user, which is the only place a
 *    typo in an email address becomes visible (the check script cannot reach
 *    the user table in CI).
 *
 * Gated on Permit.FEATURES_READ (admin + engineer); the route layer does
 * the role gating in `app/routes.tsx`.
 */

import type { JSX } from "react";
import { useCallback, useEffect, useState } from "react";

import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { FeatureState, FeaturesState } from "@shared-core/data/api-types";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../app/components/primitives/card";

export function SettingsFeaturesPage(): JSX.Element {
  const adapter = useDataAdapter();

  const [state, setState] = useState<FeaturesState | null>(null);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");

  const load = useCallback(async () => {
    try {
      setState(await adapter.getFeatures());
      setPhase("ready");
    } catch {
      setPhase("error");
    }
  }, [adapter]);

  useEffect(() => {
    void load();
  }, [load]);

  if (phase === "loading") {
    return (
      <PageShell>
        <div className="text-muted-foreground">Loading…</div>
      </PageShell>
    );
  }

  if (phase === "error" || state === null) {
    return (
      <PageShell>
        <div className="text-destructive">
          Could not load the feature state. Please refresh and try again.
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell environment={state.environment}>
      <KillSwitchCard state={state} />
      {state.features.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-muted-foreground">
            No features are declared. That is the healthy resting state — a
            feature that never reaches deletion has quietly become
            configuration.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {state.features.map((feature) => (
            <FeatureCard
              key={feature.name}
              feature={feature}
              environment={state.environment}
            />
          ))}
        </div>
      )}
    </PageShell>
  );
}

// ---------------------------------------------------------------- pieces ---

function PageShell({
  children,
  environment,
}: {
  children: React.ReactNode;
  environment?: string;
}): JSX.Element {
  return (
    <div className="max-w-4xl space-y-6">
      <header>
        <div className="flex items-baseline gap-3">
          <h1 className="text-2xl font-semibold">Features</h1>
          {environment && (
            <span
              className="rounded-md bg-muted px-2 py-0.5 text-xs font-medium uppercase tracking-wide"
              data-testid="features-environment"
            >
              {environment}
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Declared in <code>modules/features.yaml</code> and shown as this
          environment resolves them. Read-only — features change through pull
          requests.
        </p>
      </header>
      {children}
    </div>
  );
}

function KillSwitchCard({ state }: { state: FeaturesState }): JSX.Element {
  const hasOverride =
    state.forced_off.length > 0 || state.unknown_forced_off.length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Emergency override</CardTitle>
        <CardDescription>
          <code>FEATURES_FORCE_OFF</code> in this environment. It can only force
          a feature off, never on, and takes effect when the API is recreated (a
          restart does not re-read <code>api.env</code>).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {!hasOverride && (
          <p className="text-sm text-muted-foreground">
            No override is active.
          </p>
        )}
        {state.forced_off.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              In force
            </p>
            <p className="text-sm font-medium">{state.forced_off.join(", ")}</p>
          </div>
        )}
        {state.unknown_forced_off.length > 0 && (
          <div
            className="rounded-md border border-destructive/40 bg-destructive/5 p-3"
            data-testid="features-unknown-override"
          >
            <p className="text-xs uppercase tracking-wide text-destructive">
              Did not take effect
            </p>
            <p className="text-sm font-medium">
              {state.unknown_forced_off.join(", ")}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              These names match no declared feature, so they were ignored. Check
              the spelling — nothing has been disabled by them.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function FeatureCard({
  feature,
  environment,
}: {
  feature: FeatureState;
  environment: string;
}): JSX.Element {
  const allowlistHere = feature.enabled_for[environment] ?? [];
  const finished = ["dev", "staging", "prod"].every((env) =>
    feature.enabled_in.includes(env),
  );

  return (
    <Card data-testid={`feature-${feature.name}`}>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="font-mono text-base">{feature.name}</CardTitle>
          <StateBadge
            feature={feature}
            allowlistedHere={allowlistHere.length > 0}
          />
          {finished && (
            <Badge tone="warning">Enabled everywhere — ready to remove</Badge>
          )}
        </div>
        <CardDescription>{feature.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="Owner" value={feature.owner} />
          <Field label="Created" value={feature.created} />
          <Field
            label="On for everyone in"
            value={feature.enabled_in.join(", ") || "nowhere yet"}
          />
        </dl>

        {Object.keys(feature.enabled_for).length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Named trials
            </p>
            <ul className="mt-1 space-y-1 text-sm">
              {Object.entries(feature.enabled_for).map(([env, addresses]) => (
                <li key={env}>
                  <span className="font-medium">{env}</span>:{" "}
                  {addresses.join(", ")}
                </li>
              ))}
            </ul>
          </div>
        )}

        {feature.unknown_allowlist_entries.length > 0 && (
          <div
            className="rounded-md border border-destructive/40 bg-destructive/5 p-3"
            data-testid={`feature-unknown-allowlist-${feature.name}`}
          >
            <p className="text-xs uppercase tracking-wide text-destructive">
              Matches no known user
            </p>
            <p className="text-sm font-medium">
              {feature.unknown_allowlist_entries.join(", ")}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Allowlists match a user's primary address only, so these entries
              will never grant the feature to anyone.
            </p>
          </div>
        )}

        {/* Absent on a permanent feature — one that stays in the file and
            only widens its environments as it matures. Only a temporary
            feature, replacing an existing code path, carries a removal
            trigger. */}
        {feature.remove_when ? (
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Remove when
            </p>
            <p className="text-sm">{feature.remove_when}</p>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function StateBadge({
  feature,
  allowlistedHere,
}: {
  feature: FeatureState;
  allowlistedHere: boolean;
}): JSX.Element {
  if (feature.forced_off) return <Badge tone="danger">Forced off</Badge>;
  if (feature.enabled_here) return <Badge tone="success">On here</Badge>;
  if (allowlistedHere) return <Badge tone="info">Named trial here</Badge>;
  return <Badge tone="muted">Off here</Badge>;
}

const BADGE_TONES = {
  success: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  danger: "bg-destructive/10 text-destructive",
  warning: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
  info: "bg-primary/10 text-primary",
  muted: "bg-muted text-muted-foreground",
} as const;

function Badge({
  tone,
  children,
}: {
  tone: keyof typeof BADGE_TONES;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <span
      className={`rounded-md px-2 py-0.5 text-xs font-medium ${BADGE_TONES[tone]}`}
    >
      {children}
    </span>
  );
}

function Field({
  label,
  value,
}: {
  label: string;
  value: string;
}): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm font-medium">{value}</dd>
    </div>
  );
}
